import React from "react";
import ConfirmationModal from "../ConfirmationModal.jsx";
import { SECONDARY } from "./Fields.jsx";
import { useActionFeedback } from "./ActionFeedback.jsx";

const DESCRIPTION = "La version validée restera dans l’historique.\nLa saisie sera rouverte et devra être validée de nouveau après correction.";
const GENERIC_WARNING = "Un brouillon de relevé de transport lié se mettra à jour seul après la nouvelle validation. Un relevé de transport déjà numéroté doit d’abord être annulé dans Facturation.";
const PROMPT = { label: "Motif de la correction", required: true, minLength: 3 };

function InvoiceLinks({ invoices }) {
  if (!invoices.length) return null;
  return <ul className="mt-2 grid gap-1 text-[0.82rem]">
    {invoices.map(invoice => <li key={invoice.id}><a className="font-semibold text-accent-strong underline" href={invoice.url}>Relevé {invoice.number}</a></li>)}
  </ul>;
}

// Describes the real effects of reopening; POST /reopen remains the final authority.
function impactView(raw, loading) {
  const impact = raw && ["drafts", "issued", "orders_to_unapprove"].every(key => Array.isArray(raw[key])) ? raw : null;
  if (loading) return { description: `${DESCRIPTION}\nVérification des relevés de transport liés…`, warning: "", tone: "accent", links: [], blocked: false, pending: true };
  if (!impact) return { description: DESCRIPTION, warning: GENERIC_WARNING, tone: "accent", links: [], blocked: false };
  if (impact.issued.length || impact.blocked_reason) {
    const warning = impact.issued.length
      ? (impact.issued.length > 1
        ? `Relevés de transport ${impact.issued.map(i => i.number).join(", ")} déjà numérotés : annulez-les d’abord dans Facturation.`
        : `Relevé de transport ${impact.issued[0].number} déjà numéroté : annulez-le d’abord dans Facturation.`)
      : impact.blocked_message;
    return { description: DESCRIPTION, warning, tone: "danger", links: impact.issued, blocked: true };
  }
  const count = impact.orders_to_unapprove.length;
  const orders = count ? `\n${count} commande${count > 1 ? "s" : ""} livrée${count > 1 ? "s" : ""} ${count > 1 ? "repasseront" : "repassera"} à approuver.` : "";
  const warning = impact.drafts.length
    ? (impact.drafts.length > 1 ? "Les brouillons de relevé de transport liés se mettront à jour seuls après la nouvelle validation." : "Le brouillon de relevé de transport lié se mettra à jour seul après la nouvelle validation.")
    : "";
  return { description: DESCRIPTION + orders, warning, tone: "accent", links: [], blocked: false };
}

export default function ReopenButton({ label, row, disabled, busy, run, onReopen, loadImpact, openRef }) {
  const [opened, setOpened] = React.useState(false);
  const [impact, setImpact] = React.useState({ loading: false, data: null });
  const request = React.useRef(0);
  const feedback = useActionFeedback(run, row);
  const open = () => {
    feedback.clearError();
    setOpened(true);
    if (!loadImpact) return setImpact({ loading: false, data: null });
    const id = ++request.current;
    setImpact({ loading: true, data: null });
    // A failed check falls back to the generic warning; the backend still refuses when needed.
    Promise.resolve().then(loadImpact).then(data => data, () => null)
      .then(data => { if (request.current === id) setImpact({ loading: false, data }); });
  };
  const close = () => { request.current += 1; setOpened(false); };
  // Le menu du clic droit ouvre la même confirmation.
  React.useImperativeHandle(openRef, () => ({ open }));
  const confirm = ({ textPromptValue }) => feedback.execute(async () => {
    await onReopen(textPromptValue);
    close();
  }, "reopen");
  const view = impactView(impact.data, impact.loading);
  return <>
    <button type="button" className={SECONDARY} disabled={disabled} onClick={open}>Modifier</button>
    {opened ? <ConfirmationModal title={`Modifier ${label} validé ?`} eyebrow="Consolidation"
      description={view.description} warningMessage={view.warning}
      tone={view.tone} cornerRadius="grain" panelSize="narrow" contentDensity="compact"
      textPrompt={view.blocked ? null : PROMPT} confirmDisabled={view.blocked || Boolean(view.pending)}
      confirmLabel="Rouvrir pour modifier" isBusy={busy} errorMessage={feedback.error?.message}
      onCancel={() => { if (!busy) close(); }} onConfirm={confirm}>
      <InvoiceLinks invoices={view.links} />
    </ConfirmationModal> : null}
  </>;
}
